import { AnchorLayerType, LightMapAssistType } from '@/utils/constant';

export const DRAWING_STYLE = {
  point: {
    size: 12,
    fill: '#4397f7',
    border: '2px solid #ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  line: {
    strokeColor: '#4397f7',
    strokeWeight: 4,
    strokeOpacity: 0.9,
    /** 绘制过程中会导致自相交时的警示色 */
    errorColor: '#ff4d4f',
  },
  polygon: {
    strokeColor: '#4397f7',
    strokeWeight: 4,
    strokeOpacity: 0.9,
    fillColor: '#4397f7',
    fillOpacity: 0.15,
  },
  /** 吸附区域环形指示器样式（起点外围的吸附半径圈） */
  snapRing: {
    size: 22,
    borderWidth: 2,
    borderColor: '#ff4d4f',
    background: 'rgba(255,77,79,0.12)',
  },
};

/** 绘制期边长 Label 样式 */
export const DRAWING_EDGE_LABEL_STYLE = {
  border: 'none',
  background: 'rgba(0, 0, 0, 0.6)',
  color: '#fff',
  fontSize: '11px',
  padding: '2px 6px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
};

/** 车道线（限速区域）统一样式 */
export const LANE_LINE_STYLE = {
  color: '#CA60FF',
  width: 4,
  lineDash: [10, 10],
};

export interface AnchorLayerStyle {
  normalFillColor: string;
  selectedFillColor: string;
  borderColor: string;
  listBadgeText: string;
  listBadgeBackground: string;
  listBadgeBorder: string;
  legendFillColor: string;
  legendBorderColor: string;
}

/**
 * 动态地图各业务类型的统一视觉配置
 *
 * 作为地图多边形、右侧列表徽章、图例等组件的单一数据源，
 * 避免在 OperateList / Legend / ViewStrategy 等地重复维护颜色值。
 */
export const ANCHOR_LAYER_STYLE: Record<AnchorLayerType, AnchorLayerStyle> = {
  [AnchorLayerType.MOVE_ROUND]: {
    normalFillColor: 'rgba(255,158,11,0.5)',
    selectedFillColor: 'rgba(255,158,11,1)',
    borderColor: 'rgba(255,158,11,1)',
    listBadgeText: '绕',
    listBadgeBackground: 'rgba(255,158,11,0.5)',
    listBadgeBorder: '1px solid rgba(255,158,11,1)',
    legendFillColor: 'rgba(255,158,11,0.5)',
    legendBorderColor: 'rgba(255,158,11,1)',
  },
  [AnchorLayerType.ROAD_CLOSED]: {
    normalFillColor: 'rgba(255,87,77,0.5)',
    selectedFillColor: 'rgba(255,87,77,1)',
    borderColor: 'rgba(255,87,77,1)',
    listBadgeText: '封',
    listBadgeBackground: 'rgba(255,87,77,0.5)',
    listBadgeBorder: '1px solid rgba(255,87,77,1)',
    legendFillColor: 'rgba(255,87,77,0.5)',
    legendBorderColor: 'rgba(255,87,77,1)',
  },
  [AnchorLayerType.NO_PARKING]: {
    normalFillColor: 'rgba(0,162,255,0.4)',
    selectedFillColor: 'rgba(13,133,255,1)',
    borderColor: 'rgba(13,133,255,1)',
    listBadgeText: '停',
    listBadgeBackground: 'rgba(0,162,255,0.4)',
    listBadgeBorder: '1px solid rgba(13,133,255,1)',
    legendFillColor: 'rgba(13,133,255,0.5)',
    legendBorderColor: 'rgba(13,133,255,1)',
  },
  [AnchorLayerType.FOCUS_AREAS]: {
    normalFillColor: 'rgba(255,102,159,0.4)',
    selectedFillColor: 'rgba(255,102,159,1)',
    borderColor: 'rgba(255,102,159,1)',
    listBadgeText: '重',
    listBadgeBackground: 'rgba(255,102,159,0.4)',
    listBadgeBorder: '1px solid rgba(255,102,159,1)',
    legendFillColor: 'rgba(255,102,159,0.4)',
    legendBorderColor: 'rgba(255,102,159,1)',
  },
  [AnchorLayerType.ATTRIBUTE_ANNOTATION]: {
    normalFillColor: 'rgba(20,186,183,0.4)',
    selectedFillColor: 'rgba(20,186,183,1)',
    borderColor: 'rgba(20,186,183,1)',
    listBadgeText: '属',
    listBadgeBackground: 'rgba(20,186,183,0.4)',
    listBadgeBorder: '1px solid rgba(20,186,183,1)',
    legendFillColor: 'rgba(20,186,183,0.5)',
    legendBorderColor: 'rgba(20,186,183,1)',
  },
  [AnchorLayerType.SPEED_LIMIT]: {
    normalFillColor: 'rgba(202,96,255,0.5)',
    selectedFillColor: 'rgba(202,96,255,0.5)',
    borderColor: 'rgba(202,96,255,1)',
    listBadgeText: '速',
    listBadgeBackground: 'rgba(202,96,255,0.5)',
    listBadgeBorder: '1px solid rgba(202,96,255,1)',
    legendFillColor: 'rgba(202,96,255,0.5)',
    legendBorderColor: 'rgba(202,96,255,1)',
  },
  [AnchorLayerType.AIR_GROUND_GEOFENCE]: {
    normalFillColor: 'rgba(255,193,7,0.4)',
    selectedFillColor: 'rgba(255,193,7,1)',
    borderColor: 'rgba(255,193,7,1)',
    listBadgeText: '围',
    listBadgeBackground: 'rgba(255,193,7,0.5)',
    listBadgeBorder: '1px solid rgba(255,193,7,1)',
    legendFillColor: 'rgba(255,193,7,0.5)',
    legendBorderColor: 'rgba(255,193,7,1)',
  },
  [AnchorLayerType.ROAD_TEST_FOCUS_AREA]: {
    normalFillColor: 'rgba(82,196,26,0.4)',
    selectedFillColor: 'rgba(82,196,26,1)',
    borderColor: 'rgba(82,196,26,1)',
    listBadgeText: '测',
    listBadgeBackground: 'rgba(82,196,26,0.5)',
    listBadgeBorder: '1px solid rgba(82,196,26,1)',
    legendFillColor: 'rgba(82,196,26,0.5)',
    legendBorderColor: 'rgba(82,196,26,1)',
  },
  [AnchorLayerType.LD_ASSIST]: {
    normalFillColor: 'rgba(82,196,26,0.35)',
    selectedFillColor: '#52c41a',
    borderColor: '#52c41a',
    listBadgeText: '缓',
    listBadgeBackground: 'rgba(82,196,26,0.35)',
    listBadgeBorder: '1px solid #52c41a',
    legendFillColor: 'rgba(82,196,26,0.35)',
    legendBorderColor: '#52c41a',
  },
  [AnchorLayerType.NAVIGATION]: {
    normalFillColor: 'rgba(22,119,255,0.35)',
    selectedFillColor: '#1677ff',
    borderColor: '#1677ff',
    listBadgeText: '导',
    listBadgeBackground: 'rgba(22,119,255,0.35)',
    listBadgeBorder: '1px solid #1677ff',
    legendFillColor: 'rgba(22,119,255,0.35)',
    legendBorderColor: '#1677ff',
  },
  [AnchorLayerType.NO_HORN_ZONE]: {
    normalFillColor: 'rgba(89,89,89,0.35)',
    selectedFillColor: '#595959',
    borderColor: '#595959',
    listBadgeText: '禁',
    listBadgeBackground: 'rgba(89,89,89,0.35)',
    listBadgeBorder: '1px solid #595959',
    legendFillColor: 'rgba(89,89,89,0.35)',
    legendBorderColor: '#595959',
  },
};

const LIGHT_MAP_SUB_TYPE_STYLE: Record<string, {
  normal: string;
  selected: string;
  listBadgeText: string;
  listBadgeBackground: string;
  listBadgeBorder: string;
}> = {
  [LightMapAssistType.BUFFER_ZONE]: {
    normal: 'rgba(82,196,26,0.35)',
    selected: '#52c41a',
    listBadgeText: '缓',
    listBadgeBackground: 'rgba(82,196,26,0.35)',
    listBadgeBorder: '1px solid #52c41a',
  },
  [LightMapAssistType.PARKING_ZONE]: {
    normal: 'rgba(89,126,247,0.35)',
    selected: '#597ef7',
    listBadgeText: '停',
    listBadgeBackground: 'rgba(89,126,247,0.35)',
    listBadgeBorder: '1px solid #597ef7',
  },
  [LightMapAssistType.WARNING_ZONE]: {
    normal: 'rgba(245,34,45,0.35)',
    selected: '#f5222d',
    listBadgeText: '危',
    listBadgeBackground: 'rgba(245,34,45,0.35)',
    listBadgeBorder: '1px solid #f5222d',
  },
};

/** 获取动态地图类型的统一视觉配置 */
export const getAnchorLayerStyle = (
  type: AnchorLayerType,
  subType?: string
): AnchorLayerStyle => {
  const base = ANCHOR_LAYER_STYLE[type] ?? ANCHOR_LAYER_STYLE[AnchorLayerType.MOVE_ROUND];
  if (type === AnchorLayerType.LD_ASSIST && subType) {
    const override = LIGHT_MAP_SUB_TYPE_STYLE[subType];
    if (override) {
      return {
        ...base,
        normalFillColor: override.normal,
        selectedFillColor: override.selected,
        borderColor: override.selected,
        listBadgeText: override.listBadgeText,
        listBadgeBackground: override.listBadgeBackground,
        listBadgeBorder: override.listBadgeBorder,
      };
    }
  }
  return base;
};

/** 获取动态地图类型边框色 */
export const returnAnchorBorderColor = (type: AnchorLayerType, subType?: string): string =>
  getAnchorLayerStyle(type, subType).borderColor;

/** 获取动态地图类型默认填充色 */
export const returnNormalAnchorFillColor = (type: AnchorLayerType, subType?: string): string =>
  getAnchorLayerStyle(type, subType).normalFillColor;

/** 获取动态地图类型选中填充色 */
export const returnSelectedAnchorFillColor = (
  type: AnchorLayerType,
  subType?: string
): string => getAnchorLayerStyle(type, subType).selectedFillColor;
